import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { createStripeCheckoutSession } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { trackInitiateCheckout } from "@/lib/metaPixel";

const Field = ({ label, name, value, onChange, type = "text", required = true, testid, placeholder, error, help, autoComplete, inputMode, enterKeyHint }) => (
  <label className="block">
    <span className="eyebrow mb-2 block">{label}</span>
    <input
      type={type}
      name={name}
      value={value}
      required={required}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      autoComplete={autoComplete}
      inputMode={inputMode}
      enterKeyHint={enterKeyHint}
      aria-invalid={error ? "true" : "false"}
      className={`w-full bg-transparent border-b outline-none py-3 text-[16px] transition-colors ${
        error ? "border-[#a33] focus:border-[#a33]" : "border-[#dcd7ce] focus:border-[#1A1A1A]"
      }`}
    />
    {error ? (
      <span className="mt-2 block text-xs text-[#a33]" data-testid={`${testid}-error`}>{error}</span>
    ) : help ? (
      <span className="mt-2 block text-xs text-[#8a8378]">{help}</span>
    ) : null}
  </label>
);

const PHONE_RE = /^\+?[\d\s\-().]{8,}$/;

const Checkout = () => {
  const { items, subtotal, shipping, total } = useCart();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isFr = lang === "fr";

  const phoneInvalidMsg = isFr
    ? "Merci de renseigner un numéro de téléphone valide (nécessaire pour la livraison)"
    : "Please enter a valid phone number (required for delivery)";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!PHONE_RE.test(phone.trim())) {
      setPhoneError(phoneInvalidMsg);
      toast.error(phoneInvalidMsg);
      return;
    }
    setPhoneError("");
    if (!acceptCgv) {
      toast.error(isFr ? "Veuillez accepter les CGV pour continuer" : "Please accept the Terms to continue");
      return;
    }
    setSubmitting(true);
    // Meta Pixel — InitiateCheckout
    trackInitiateCheckout({
      ids: items.map((i) => `${i.product_id}::${i.variant_id}`),
      value: total,
      numItems: items.reduce((n, i) => n + i.quantity, 0),
      currency: "EUR",
    });
    try {
      const res = await createStripeCheckoutSession({
        items: items.map((i) => ({
          product_id: i.product_id,
          variant_id: i.variant_id,
          quantity: i.quantity,
        })),
        email,
        phone: phone.trim(),
        lang,
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      toast.error(isFr ? "Impossible d'initier le paiement" : "Could not start payment");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(detail || (isFr ? "Erreur lors de la commande" : "Order error"));
    } finally {
      setSubmitting(false);
    }
  };

  // Empty cart
  if (items.length === 0) {
    return (
      <section
        data-testid="checkout-empty"
        className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center"
      >
        <h1 className="font-serif-display text-3xl md:text-4xl leading-tight">
          {t.cart.empty}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/")}
          data-testid="checkout-back-empty"
          className="mt-8 inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F0EB] px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-[#333] transition-colors rounded-sm"
        >
          {t.cart.emptyCta}
        </button>
      </section>
    );
  }

  return (
    <section
      data-testid="checkout-page"
      className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 py-10 md:py-16"
    >
      <Seo
        lang={lang}
        path="/checkout"
        title={isFr ? "Paiement sécurisé" : "Secure checkout"}
        description={
          isFr
            ? "Finalisez votre commande myFLOVEA. Paiement sécurisé Stripe (carte, Apple Pay, Google Pay)."
            : "Complete your myFLOVEA order. Secure Stripe checkout (card, Apple Pay, Google Pay)."
        }
      />
      <Link
        to="/"
        data-testid="checkout-back"
        className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase hover:opacity-60 mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.4} />
        {t.checkout.back}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="checkout-form"
          className="lg:col-span-7"
        >
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">
            {t.checkout.title}
          </h1>
          <p className="mt-4 text-[15px] text-[#4a4a4a] max-w-md leading-relaxed">
            {isFr
              ? "Laissez votre email — vous serez redirigé vers notre page de paiement sécurisée Stripe pour finaliser la commande (carte, Apple Pay, Google Pay)."
              : "Leave your email — you'll be redirected to our secure Stripe checkout to complete your order (card, Apple Pay, Google Pay)."}
          </p>

          <div className="mt-12">
            <div className="eyebrow mb-6">{t.checkout.contact}</div>
            <Field
              label={t.checkout.fields.email}
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              testid="checkout-email"
              autoComplete="email"
              inputMode="email"
              enterKeyHint="next"
              placeholder="prenom@email.com"
            />
            <div className="mt-6">
              <Field
                label={isFr ? "Téléphone" : "Phone"}
                name="phone"
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); if (phoneError) setPhoneError(""); }}
                testid="checkout-phone"
                placeholder="+33 6 12 34 56 78"
                autoComplete="tel"
                inputMode="tel"
                enterKeyHint="done"
                error={phoneError}
                help={isFr
                  ? "Obligatoire — indispensable pour la livraison et les douanes."
                  : "Required — necessary for delivery and customs."}
              />
            </div>
          </div>

          <div className="mt-10 bg-[#ebe7df]/60 border border-[#dcd7ce] p-5 rounded-sm">
            <div className="flex items-start gap-3">
              <ShieldCheck size={18} strokeWidth={1.4} className="mt-0.5 shrink-0 text-[#3D2B1F]" />
              <div className="text-sm text-[#3D2B1F] leading-relaxed">
                <div className="font-medium mb-1">
                  {isFr ? "Paiement sécurisé SSL via Stripe" : "Secure SSL payment via Stripe"}
                </div>
                <p className="text-[#4a4a4a]">
                  {isFr
                    ? "Adresse de livraison et éventuel code promo seront demandés à l'étape suivante. Livraison gratuite en Europe sous 5–10 jours."
                    : "Shipping address and any promo code will be requested at the next step. Free European delivery in 5–10 days."}
                </p>
              </div>
            </div>
          </div>

          <label
            className="mt-6 flex items-start gap-3 cursor-pointer py-2"
            data-testid="checkout-cgv-label"
          >
            <input
              type="checkbox"
              checked={acceptCgv}
              onChange={(e) => setAcceptCgv(e.target.checked)}
              required
              data-testid="checkout-cgv-checkbox"
              className="mt-1 shrink-0 w-5 h-5"
            />
            <span className="text-xs text-[#3D2B1F] leading-relaxed">
              {isFr ? (
                <>
                  J&apos;ai lu et j&apos;accepte les{" "}
                  <Link to="/legal/cgv" className="underline hover:opacity-70" target="_blank" rel="noreferrer">
                    Conditions Générales de Vente
                  </Link>
                  {" "}et la{" "}
                  <Link to="/legal/privacy" className="underline hover:opacity-70" target="_blank" rel="noreferrer">
                    Politique de confidentialité
                  </Link>
                  {" "}de myFLOVEA (EM GROUP SASU).
                </>
              ) : (
                <>
                  I have read and accept the{" "}
                  <Link to="/legal/cgv" className="underline hover:opacity-70" target="_blank" rel="noreferrer">
                    Terms of Sale
                  </Link>
                  {" "}and{" "}
                  <Link to="/legal/privacy" className="underline hover:opacity-70" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </Link>
                  {" "}of myFLOVEA (EM GROUP SASU).
                </>
              )}
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting || !acceptCgv}
            data-testid="place-order-button"
            className="mt-10 w-full bg-[#3D2B1F] text-white py-5 text-sm tracking-[0.22em] uppercase font-medium hover:bg-[#1A1A1A] hover:-translate-y-0.5 disabled:opacity-60 transition-all duration-300 rounded-md shadow-[0_10px_30px_-12px_rgba(61,43,31,0.55)] inline-flex items-center justify-center gap-3"
          >
            <Lock size={14} strokeWidth={1.8} />
            {submitting
              ? (isFr ? "Redirection…" : "Redirecting…")
              : (isFr ? "Payer avec Stripe" : "Pay with Stripe")}
          </button>
        </form>

        {/* Summary */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-[#ebe7df]/50 border border-[#dcd7ce] p-8 md:p-10 rounded-sm">
            <div className="eyebrow mb-6">{t.checkout.summary}</div>
            <ul className="space-y-5 divide-y divide-[#dcd7ce]">
              {items.map((item) => (
                <li
                  key={item.key}
                  data-testid={`summary-item-${item.key}`}
                  className="flex gap-4 pt-5 first:pt-0"
                >
                  <div className="w-16 h-20 overflow-hidden bg-[#dcd7ce] rounded-sm shrink-0">
                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-lg leading-snug">
                      {item.product_name[lang]}
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-0.5">
                      {item.variant_name[lang]}
                    </div>
                    <div className="text-xs text-[#4a4a4a] mt-1">
                      × {item.quantity}
                    </div>
                  </div>
                  <div className="text-sm">
                    {(item.price * item.quantity).toFixed(2)} {t.common.currency}
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-3 border-t border-[#dcd7ce] pt-6">
              <div className="flex justify-between text-sm">
                <span className="text-[#4a4a4a]">{t.cart.subtotal}</span>
                <span>
                  {subtotal.toFixed(2)} {t.common.currency}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#4a4a4a]">{t.cart.shipping}</span>
                <span>
                  {shipping === 0 ? t.cart.shippingFree : `${shipping.toFixed(2)} ${t.common.currency}`}
                </span>
              </div>
              <div className="flex justify-between text-sm text-[#8a6d3b]">
                <span>{isFr ? "Code promo" : "Promo code"}</span>
                <span>
                  {isFr ? "À l'étape Stripe" : "At Stripe step"}
                </span>
              </div>
              <div className="flex justify-between pt-4 border-t border-[#dcd7ce]">
                <span className="font-serif-display text-lg">{t.cart.total}</span>
                <span className="font-serif-display text-lg" data-testid="checkout-total">
                  {total.toFixed(2)} {t.common.currency}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default Checkout;
